/**
 * Admin — accounts, teams, and the audit log. Built to the Parallax design:
 * a centred glass card over a radial-dark canvas, with a segmented tab bar.
 *
 * Opens from Group Commander up. A Group Commander sees its own teams and the
 * people in them, and can hand out roles only up to Tactical; the server
 * decides all of that and scopes every list it returns, so this screen only
 * keeps from offering a control the server would refuse.
 *
 * Server rules show through the UI on purpose, because a hidden control
 * explains itself and one that errors does not: nobody can demote or disable
 * their own account (those controls are absent on their own row), and the
 * audit log is read-only and an admin's alone. Every mutating action is
 * recorded server-side and nothing here can edit or delete it.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { api } from '@/api/client';
import { describePorts, listTeams } from '@/api/teams';
import { cn } from '@/lib/utils';
import { useCanAdminister, useCurrentUser } from '@/stores/authStore';
import { useDroneIds } from '@/stores/droneStore';
import { ROLE_RANK, type PortRange, type Role, type Team } from '@/types/domain';
import { NetworkTab } from './NetworkTab';

import { NodesTab } from './NodesTab';

interface AdminUser {
  userId: string;
  username: string;
  role: Role;
  enabled: boolean;
  observer?: boolean;
  teamIds?: string[];
  lastLogin?: number | null;
}

interface AdminTeam {
  teamId: string;
  callsign: string;
  displayName: string;
  color: string;
  droneIds: string[];
  memberIds: string[];
  tcpPorts?: PortRange | null;
  udpPorts?: PortRange | null;
}

interface AuditRow {
  entryId: string;
  timestamp: number;
  username: string | null;
  action: string;
  resource: string;
  outcome: string;
  detail: string;
}

type TabId = 'accounts' | 'teams' | 'audit' | 'network' | 'nodes';
type AuditFilter = 'ALL' | 'COMMANDS' | 'ADMIN' | 'PLANS' | 'REFUSALS';

const MUTED = 'rgba(244,244,245,0.55)';
const OK = 'oklch(0.78 0.15 155)';
const DANGER_FG = 'oklch(0.72 0.19 28)';
const WHITE = '#f4f4f5';

const CARD =
  'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))';

function initialsOf(name: string): string {
  return name.slice(0, 2).toUpperCase();
}

function relativeTime(ts: number | null | undefined): string {
  if (!ts) return 'never';
  const secs = Math.max(0, Date.now() / 1000 - ts);
  if (secs < 45) return 'now';
  if (secs < 3600) return `${Math.round(secs / 60)} min ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)} h ago`;
  if (secs < 172800) return 'yesterday';
  return new Date(ts * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function clock(ts: number): string {
  return new Date(ts * 1000).toLocaleTimeString(undefined, { hour12: false });
}

/** Map a raw audit action/resource onto the design's COMMAND / PLAN / ADMIN / AUTH kinds. */
function auditKind(row: AuditRow): string {
  const a = row.action.toLowerCase();
  const r = row.resource.toLowerCase();
  if (a === 'flight_command' || r.startsWith('drone:')) return 'COMMAND';
  if (r.includes('/plan') || r.includes('/mission') || a.includes('plan') || a.includes('mission'))
    return 'PLAN';
  if (r.includes('/admin') || a.includes('user') || a.includes('team')) return 'ADMIN';
  if (r.includes('/auth') || a.includes('login') || a.includes('token')) return 'AUTH';
  return 'EVENT';
}

function auditFailed(row: AuditRow): boolean {
  const o = row.outcome.toLowerCase();
  return o === 'denied' || o === 'error' || o === 'refused' || o === 'failed';
}

// ---------------------------------------------------------------- tab bar

function TabBar({
  tab,
  setTab,
  showAudit,
}: {
  tab: TabId;
  setTab: (t: TabId) => void;
  showAudit: boolean;
}) {
  // The audit trail is an admin's alone; the server refuses it to anyone else.
  const tabs: Array<{ id: TabId; label: string }> = [
    { id: 'accounts', label: 'Accounts' },
    { id: 'teams', label: 'Teams' },
    ...(showAudit
      ? [
          { id: 'audit' as const, label: 'Audit log' },
          { id: 'network' as const, label: 'Network' },
          // Other nodes, Netbird and CHAOS are all admin-only on the server too.
          { id: 'nodes' as const, label: 'Nodes' },
        ]
      : []),
  ];
  return (
    <div
      className="flex max-w-full shrink-0 gap-1 self-start overflow-x-auto rounded-[99px] border p-1 [scrollbar-width:none]"
      style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.08)' }}
    >
      {tabs.map((t) => {
        const active = tab === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={(event) => {
              setTab(t.id);
              // The strip scrolls sideways on a phone; keep the chosen tab in view.
              event.currentTarget.scrollIntoView({ block: 'nearest', inline: 'nearest' });
            }}
            className="shrink-0 whitespace-nowrap rounded-[99px] px-[18px] py-[7px] text-[12px] font-semibold phone:px-3.5"
            style={{
              background: active ? 'rgba(255,255,255,0.92)' : 'transparent',
              color: active ? '#0c0c0d' : MUTED,
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function SectionMsg({ msg }: { msg: string | null }) {
  if (!msg) return null;
  const danger = /refus|denied|error|fail|cannot|forbidden/i.test(msg);
  return (
    <div
      className="mb-2.5 label-mono text-[10px]"
      style={{ color: danger ? DANGER_FG : OK, letterSpacing: '0.02em' }}
    >
      {msg}
    </div>
  );
}

// ---------------------------------------------------------------- accounts

const ALL_ROLES: Role[] = ['viewer', 'operator', 'tactical', 'group_commander', 'admin'];

/** The highest role anybody below admin may hand out or manage. Mirrors the server. */
const MAX_ROLE_BELOW_ADMIN: Role = 'tactical';

function roleLabel(role: Role): string {
  return role === 'group_commander' ? 'group cmdr' : role;
}

/** Roles this person may assign: all of them for an admin, up to Tactical otherwise. */
function assignableRoles(isAdmin: boolean): Role[] {
  return isAdmin
    ? ALL_ROLES
    : ALL_ROLES.filter((r) => ROLE_RANK[r] <= ROLE_RANK[MAX_ROLE_BELOW_ADMIN]);
}

/**
 * Whether a Group Commander may manage this account. The server checks the
 * account's current role and every team it is in; this only hides the buttons
 * that would come back refused.
 */
function canManage(isAdmin: boolean, myTeamIds: string[], target: AdminUser): boolean {
  if (isAdmin) return true;
  if (target.observer || ROLE_RANK[target.role] > ROLE_RANK[MAX_ROLE_BELOW_ADMIN]) return false;
  const theirs = target.teamIds ?? [];
  return theirs.length > 0 && theirs.every((id) => myTeamIds.includes(id));
}

function AccountsTab({
  users,
  teams,
  selfId,
  isAdmin,
  myTeamIds,
  reload,
}: {
  users: AdminUser[];
  teams: AdminTeam[];
  selfId: string | null;
  isAdmin: boolean;
  myTeamIds: string[];
  reload: () => void;
}) {
  const ROLES = assignableRoles(isAdmin);
  const [msg, setMsg] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [nu, setNu] = useState('');
  const [np, setNp] = useState('');
  const [nr, setNr] = useState<Role>('viewer');
  const [nObserver, setNObserver] = useState(false);
  // A Group Commander must put a new account in one of its teams; an admin may
  // leave it teamless and place it later.
  const [nTeam, setNTeam] = useState<string>('');
  const [resetFor, setResetFor] = useState<string | null>(null);
  const [rp, setRp] = useState('');
  const [ap, setAp] = useState('');
  const [delArmed, setDelArmed] = useState<string | null>(null);

  const teamsFor = (userId: string) =>
    teams.filter((t) => t.memberIds.includes(userId)).map((t) => t.callsign);

  const create = async () => {
    setMsg(null);
    const result = await api.post(
      '/admin/users',
      {
        username: nu.trim(),
        password: np,
        role: nObserver ? 'viewer' : nr,
        observer: nObserver,
        ...(nTeam ? { team_id: nTeam } : {}),
      },
      { label: `create user ${nu.trim()}` },
    );
    if (!result.ok) return setMsg(result.error.message);
    setNu('');
    setNp('');
    setNr('viewer');
    setNObserver(false);
    setNTeam('');
    setCreating(false);
    setMsg(
      nObserver
        ? `Observer ${nu.trim()} created. Sees every aircraft, can change nothing.`
        : `Operator ${nu.trim()} created (role ${roleLabel(nr)})`,
    );
    reload();
  };

  const patch = async (u: AdminUser, changes: Record<string, unknown>, note: string) => {
    setMsg(null);
    const result = await api.patch(`/admin/users/${encodeURIComponent(u.userId)}`, changes, {
      label: 'update user',
    });
    if (!result.ok) return setMsg(result.error.message);
    setMsg(note);
    reload();
  };

  const resetPassword = async (u: AdminUser) => {
    setMsg(null);
    // The server re-authenticates the admin before overwriting a credential —
    // a walked-away-from admin session must not be enough to take an account.
    const result = await api.post(
      `/admin/users/${encodeURIComponent(u.userId)}/password`,
      { admin_password: ap, new_password: rp },
      { label: `reset password ${u.username}` },
    );
    if (!result.ok) return setMsg(result.error.message);
    setResetFor(null);
    setRp('');
    setAp('');
    setMsg(`${u.username}'s password reset — their active sessions are revoked`);
  };

  const removeUser = async (u: AdminUser) => {
    setMsg(null);
    const result = await api.delete(`/admin/users/${encodeURIComponent(u.userId)}`, {
      label: `delete user ${u.username}`,
    });
    if (!result.ok) return setMsg(result.error.message);
    setDelArmed(null);
    setMsg(`${u.username} deleted`);
    reload();
  };

  return (
    <div className="p-[18px]">
      <div className="mb-3 flex flex-wrap items-center gap-2.5">
        <span className="label-mono text-[10px]">ACCOUNTS</span>
        <span className="label-mono text-[10.5px]" style={{ color: MUTED }}>
          {users.length}
        </span>
        <button
          type="button"
          onClick={() => setCreating((v) => !v)}
          className="ml-auto rounded-[99px] px-[14px] py-[7px] label-mono text-[9.5px] font-bold"
          style={{ background: 'rgba(255,255,255,0.9)', color: '#0c0c0d', letterSpacing: '0.06em' }}
        >
          + NEW OPERATOR
        </button>
      </div>

      <SectionMsg msg={msg} />

      {creating ? (
        <div
          className="mb-3 flex flex-wrap items-end gap-2 rounded-[13px] border p-3"
          style={{ background: 'rgba(255,255,255,0.035)', borderColor: 'rgba(255,255,255,0.06)' }}
        >
          <input
            value={nu}
            onChange={(e) => setNu(e.target.value)}
            placeholder="username"
            autoComplete="off"
            className="well h-8 w-40 rounded-[99px] px-3 text-[12px] outline-none"
          />
          <input
            value={np}
            onChange={(e) => setNp(e.target.value)}
            type="password"
            placeholder="initial password"
            autoComplete="new-password"
            className="well h-8 w-44 rounded-[99px] px-3 text-[12px] outline-none"
          />
          <select
            value={nObserver ? 'viewer' : nr}
            disabled={nObserver}
            onChange={(e) => setNr(e.target.value as Role)}
            aria-label="Role"
            className="well h-8 rounded-[99px] px-3 text-[12px] outline-none label-mono disabled:opacity-50"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {roleLabel(r)}
              </option>
            ))}
          </select>
          <select
            value={nTeam}
            onChange={(e) => setNTeam(e.target.value)}
            aria-label="Team"
            className="well h-8 rounded-[99px] px-3 text-[12px] outline-none label-mono"
          >
            <option value="">{isAdmin ? 'no team yet' : 'choose a team'}</option>
            {teams.map((t) => (
              <option key={t.teamId} value={t.teamId}>
                {t.callsign}
              </option>
            ))}
          </select>
          {isAdmin ? (
            <label
              className="flex h-8 items-center gap-1.5 text-[11px]"
              style={{ color: MUTED }}
              title="Sees every aircraft in the operation and can change nothing. Always a viewer."
            >
              <input
                type="checkbox"
                checked={nObserver}
                onChange={(e) => setNObserver(e.target.checked)}
              />
              observer
            </label>
          ) : null}
          <button
            type="button"
            disabled={!nu.trim() || !np || (!isAdmin && !nTeam)}
            onClick={() => void create()}
            className="rounded-[99px] px-[14px] py-2 label-mono text-[9.5px] font-bold disabled:opacity-40"
            style={{ background: 'rgba(255,255,255,0.9)', color: '#0c0c0d' }}
          >
            CREATE
          </button>
        </div>
      ) : null}

      <div className="flex flex-col gap-1">
        {users.map((u) => {
          const isSelf = u.userId === selfId;
          const manageable = canManage(isAdmin, myTeamIds, u);
          const roleUpper = roleLabel(u.role).toUpperCase();
          const userTeams = teamsFor(u.userId);
          return (
            <div
              key={u.userId}
              className="flex flex-wrap items-center gap-3 rounded-[13px] border px-3 py-[9px] text-[12px]"
              style={{
                background: 'rgba(255,255,255,0.035)',
                borderColor: 'rgba(255,255,255,0.05)',
                rowGap: 6,
                opacity: u.enabled ? 1 : 0.6,
              }}
            >
              <span className="flex min-w-[150px] flex-1 items-center gap-[9px]">
                <span
                  className="inline-flex size-6 shrink-0 items-center justify-center rounded-full label-mono text-[9px]"
                  style={{ background: 'rgba(255,255,255,0.12)' }}
                >
                  {initialsOf(u.username)}
                </span>
                <span className="tnum min-w-0 truncate" title={u.username}>
                  {u.username}
                </span>
                {isSelf ? (
                  <span
                    className="rounded-[99px] border px-1.5 py-px label-mono text-[8px]"
                    style={{ color: MUTED, borderColor: 'rgba(255,255,255,0.14)' }}
                  >
                    YOU
                  </span>
                ) : null}
                {u.observer ? (
                  <span
                    className="rounded-[99px] border px-1.5 py-px label-mono text-[8px]"
                    style={{ color: MUTED, borderColor: 'rgba(255,255,255,0.14)' }}
                    title="Sees every aircraft in the operation and can change nothing"
                  >
                    OBSERVER
                  </span>
                ) : null}
              </span>

              <span className="w-24 shrink-0">
                {editing === u.userId && !isSelf && manageable && !u.observer ? (
                  <select
                    value={u.role}
                    onChange={(e) => {
                      setEditing(null);
                      void patch(u, { role: e.target.value }, `${u.username} role → ${e.target.value}`);
                    }}
                    className="well h-6 rounded-[99px] px-2 text-[11px] outline-none label-mono"
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {roleLabel(r)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span
                    className="rounded-[99px] border px-[9px] py-[3px] label-mono text-[9px]"
                    style={{
                      color: roleUpper === 'ADMIN' ? WHITE : MUTED,
                      borderColor: 'rgba(255,255,255,0.14)',
                      background: 'rgba(255,255,255,0.05)',
                      letterSpacing: '0.06em',
                    }}
                  >
                    {roleUpper}
                  </span>
                )}
              </span>

              <span
                className="tnum min-w-0 flex-1 truncate text-[11px]"
                style={{ color: 'rgba(244,244,245,0.6)', flexBasis: 130 }}
                title={userTeams.join(' · ') || undefined}
              >
                {userTeams.length ? userTeams.join(' · ') : '—'}
              </span>

              <span
                className="tnum w-[110px] shrink-0 text-[10.5px]"
                style={{ color: 'rgba(244,244,245,0.45)' }}
              >
                {relativeTime(u.lastLogin)}
              </span>

              <span className="ml-auto flex gap-1.5 whitespace-nowrap">
                {isSelf ? (
                  <span className="py-1 text-[9.5px]" style={{ color: 'rgba(244,244,245,0.4)' }}>
                    Self-demotion controls hidden
                  </span>
                ) : !manageable ? (
                  <span className="py-1 text-[9.5px]" style={{ color: 'rgba(244,244,245,0.4)' }}>
                    Managed by an admin
                  </span>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setEditing((e) => (e === u.userId ? null : u.userId))}
                      className="rounded-[99px] border px-[11px] py-1 label-mono text-[9px]"
                      style={{ color: 'rgba(244,244,245,0.6)', borderColor: 'rgba(255,255,255,0.14)' }}
                    >
                      EDIT
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setResetFor((r) => (r === u.userId ? null : u.userId));
                        setRp('');
                        setAp('');
                      }}
                      className="rounded-[99px] border px-[11px] py-1 label-mono text-[9px]"
                      style={{ color: 'rgba(244,244,245,0.6)', borderColor: 'rgba(255,255,255,0.14)' }}
                    >
                      RESET PW
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        void patch(
                          u,
                          { enabled: !u.enabled },
                          u.enabled
                            ? `${u.username} disabled — active sessions revoked, audit entry recorded`
                            : `${u.username} re-enabled — sessions may resume`,
                        )
                      }
                      className="rounded-[99px] border px-[11px] py-1 label-mono text-[9px]"
                      style={{
                        color: u.enabled ? DANGER_FG : OK,
                        borderColor: 'color-mix(in oklch, oklch(0.62 0.20 28) 50%, transparent)',
                        background: u.enabled
                          ? 'transparent'
                          : 'color-mix(in oklch, oklch(0.78 0.15 155) 12%, transparent)',
                      }}
                    >
                      {u.enabled ? 'DISABLE' : 'ENABLE'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (delArmed !== u.userId) {
                          setDelArmed(u.userId);
                          setMsg(`Deleting ${u.username} is permanent — press DELETE again to confirm`);
                          return;
                        }
                        void removeUser(u);
                      }}
                      className="rounded-[99px] border px-[11px] py-1 label-mono text-[9px]"
                      style={{
                        color: DANGER_FG,
                        borderColor: 'color-mix(in oklch, oklch(0.62 0.20 28) 50%, transparent)',
                        background:
                          delArmed === u.userId
                            ? 'color-mix(in oklch, oklch(0.62 0.20 28) 20%, transparent)'
                            : 'transparent',
                      }}
                    >
                      {delArmed === u.userId ? 'CONFIRM' : 'DELETE'}
                    </button>
                  </>
                )}
              </span>

              {resetFor === u.userId && !isSelf && manageable ? (
                <div className="flex w-full flex-wrap items-center gap-2 pt-1">
                  <input
                    value={rp}
                    onChange={(e) => setRp(e.target.value)}
                    type="password"
                    placeholder="new password (min 12)"
                    autoComplete="new-password"
                    className="well h-7 w-48 rounded-[99px] px-3 text-[11px] outline-none"
                  />
                  <input
                    value={ap}
                    onChange={(e) => setAp(e.target.value)}
                    type="password"
                    placeholder="YOUR password (re-auth)"
                    autoComplete="current-password"
                    className="well h-7 w-48 rounded-[99px] px-3 text-[11px] outline-none"
                  />
                  <button
                    type="button"
                    disabled={rp.length < 12 || !ap}
                    onClick={() => void resetPassword(u)}
                    className="rounded-[99px] px-3 py-1 label-mono text-[9px] font-bold disabled:opacity-40"
                    style={{ background: 'rgba(255,255,255,0.9)', color: '#0c0c0d' }}
                  >
                    SET
                  </button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- teams

interface TeamDraft {
  callsign: string;
  displayName: string;
  color: string;
  tcpStart: string;
  tcpEnd: string;
  udpStart: string;
  udpEnd: string;
}

const DEFAULT_TEAM_COLOR = '#38bdf8';

function draftOf(team: AdminTeam | null): TeamDraft {
  return {
    callsign: team?.callsign ?? '',
    displayName: team?.displayName ?? '',
    color: team?.color ?? DEFAULT_TEAM_COLOR,
    tcpStart: team?.tcpPorts ? String(team.tcpPorts.start) : '',
    tcpEnd: team?.tcpPorts ? String(team.tcpPorts.end) : '',
    udpStart: team?.udpPorts ? String(team.udpPorts.start) : '',
    udpEnd: team?.udpPorts ? String(team.udpPorts.end) : '',
  };
}

/** A blank bound is null, which is how a block is given back. */
function portOrNull(raw: string): number | null {
  const n = Number.parseInt(raw.trim(), 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * The fields to send. On an edit only what changed goes, and a block goes as
 * both of its bounds or not at all, so the server validates the whole block it
 * would become. A cleared block goes as two nulls, which is the only way to
 * give one back.
 */
function teamBody(draft: TeamDraft, base: AdminTeam | null): Record<string, unknown> {
  const before = draftOf(base);
  const body: Record<string, unknown> = {};
  const changed = (key: keyof TeamDraft) => base === null || draft[key] !== before[key];
  if (changed('callsign')) body.callsign = draft.callsign.trim().toUpperCase();
  if (changed('displayName')) body.display_name = draft.displayName.trim();
  if (changed('color')) body.color = draft.color;
  if (changed('tcpStart') || changed('tcpEnd')) {
    body.tcp_port_start = portOrNull(draft.tcpStart);
    body.tcp_port_end = portOrNull(draft.tcpEnd);
  }
  if (changed('udpStart') || changed('udpEnd')) {
    body.udp_port_start = portOrNull(draft.udpStart);
    body.udp_port_end = portOrNull(draft.udpEnd);
  }
  return body;
}

/**
 * Create or edit a team: callsign, name, colour and its port blocks. The blocks
 * other teams already hold are listed beside the port fields, with this team's
 * own left out, or its ports would read as taken by itself.
 */
function TeamForm({
  base,
  roster,
  busy,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  base: AdminTeam | null;
  roster: Team[];
  busy: boolean;
  submitLabel: string;
  onSubmit: (body: Record<string, unknown>) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<TeamDraft>(() => draftOf(base));
  const set = (key: keyof TeamDraft) => (value: string) =>
    setDraft((d) => ({ ...d, [key]: value }));
  const taken = roster
    .filter((t) => t.teamId !== base?.teamId)
    .flatMap((t) => describePorts(t).map((range) => `${t.callsign} ${range}`));
  const portInput = (key: keyof TeamDraft, label: string) => (
    <input
      value={draft[key]}
      onChange={(e) => set(key)(e.target.value.replace(/[^0-9]/g, ''))}
      inputMode="numeric"
      placeholder={label}
      aria-label={label}
      className="well h-8 w-[88px] rounded-[99px] px-3 text-[12px] outline-none tnum"
    />
  );

  return (
    <div
      className="mb-3 flex flex-col gap-2 rounded-[13px] border p-3"
      style={{ background: 'rgba(255,255,255,0.035)', borderColor: 'rgba(255,255,255,0.06)' }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={draft.callsign}
          onChange={(e) => set('callsign')(e.target.value)}
          placeholder="callsign (VIPER)"
          aria-label="Callsign"
          className="well h-8 w-36 rounded-[99px] px-3 text-[12px] outline-none"
        />
        <input
          value={draft.displayName}
          onChange={(e) => set('displayName')(e.target.value)}
          placeholder="display name (optional)"
          aria-label="Display name"
          className="well h-8 w-52 rounded-[99px] px-3 text-[12px] outline-none"
        />
        <label className="flex h-8 items-center gap-1.5 text-[11px]" style={{ color: MUTED }}>
          colour
          <input
            type="color"
            value={draft.color}
            onChange={(e) => set('color')(e.target.value)}
            aria-label="Team colour"
            className="h-6 w-8 cursor-pointer rounded border-0 bg-transparent p-0"
          />
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="w-9 label-mono text-[9px]" style={{ color: MUTED }}>
          TCP
        </span>
        {portInput('tcpStart', 'first port')}
        {portInput('tcpEnd', 'last port')}
        <span className="w-9 pl-2 label-mono text-[9px]" style={{ color: MUTED }}>
          UDP
        </span>
        {portInput('udpStart', 'first port')}
        {portInput('udpEnd', 'last port')}
      </div>
      <p className="text-[10.5px] leading-relaxed" style={{ color: MUTED }}>
        A link listening on a port in these ranges joins this team when it opens. Leave both ends
        blank for no block.
        {taken.length ? ` Already held: ${taken.join(', ')}.` : ' No other team holds a block yet.'}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={!draft.callsign.trim() || busy}
          onClick={() => onSubmit(teamBody(draft, base))}
          className="rounded-[99px] px-[14px] py-2 label-mono text-[9.5px] font-bold disabled:opacity-40"
          style={{ background: 'rgba(255,255,255,0.9)', color: '#0c0c0d' }}
        >
          {submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-[99px] border px-[14px] py-2 label-mono text-[9.5px]"
          style={{ color: MUTED, borderColor: 'rgba(255,255,255,0.14)' }}
        >
          CANCEL
        </button>
      </div>
    </div>
  );
}

function TeamsTab({
  teams,
  users,
  droneIds,
  reload,
}: {
  teams: AdminTeam[];
  users: AdminUser[];
  droneIds: string[];
  reload: () => void;
}) {
  const [msg, setMsg] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editingTeam, setEditingTeam] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [roster, setRoster] = useState<Team[]>([]);
  const [delArmed, setDelArmed] = useState<string | null>(null);

  // Every team's blocks, including teams a Group Commander is not in and so
  // does not get from /admin/teams: a new block has to avoid all of them.
  useEffect(() => {
    void (async () => {
      const result = await listTeams();
      if (result.ok) setRoster(result.data);
    })();
  }, [teams]);
  const nameFor = (id: string) => users.find((u) => u.userId === id)?.username ?? id;
  const assignedSet = new Set(teams.flatMap((t) => t.droneIds));
  const unassigned = droneIds.filter((d) => !assignedSet.has(d));

  const run = async (result: Promise<{ ok: boolean; error?: { message: string } | null }>) => {
    setMsg(null);
    const r = await result;
    if (!r.ok) return setMsg(r.error?.message ?? 'Request failed');
    reload();
  };

  const assign = (teamId: string, droneId: string) =>
    run(
      api.post(
        `/admin/teams/${encodeURIComponent(teamId)}/drones`,
        { drone_id: droneId },
        { label: `assign ${droneId}` },
      ),
    );

  const unassign = (teamId: string, droneId: string) =>
    run(
      api.delete(
        `/admin/teams/${encodeURIComponent(teamId)}/drones/${encodeURIComponent(droneId)}`,
        { label: `unassign ${droneId}` },
      ),
    );

  const addMember = (teamId: string, userId: string) =>
    run(
      api.post(
        `/teams/${encodeURIComponent(teamId)}/members`,
        { user_id: userId },
        { label: 'add team member' },
      ),
    );

  const removeMember = (teamId: string, userId: string) =>
    run(
      api.delete(
        `/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`,
        { label: 'remove team member' },
      ),
    );

  const createTeam = async (body: Record<string, unknown>) => {
    setMsg(null);
    setBusy(true);
    const result = await api.post('/admin/teams', body, {
      label: `create team ${String(body.callsign ?? '')}`,
    });
    setBusy(false);
    if (!result.ok) return setMsg(result.error.message);
    setCreating(false);
    setMsg(`Team ${String(body.callsign ?? '')} created`);
    reload();
  };

  const saveTeam = async (team: AdminTeam, body: Record<string, unknown>) => {
    setMsg(null);
    if (!Object.keys(body).length) {
      setEditingTeam(null);
      return;
    }
    setBusy(true);
    const result = await api.patch(`/admin/teams/${encodeURIComponent(team.teamId)}`, body, {
      label: `edit team ${team.callsign}`,
    });
    setBusy(false);
    if (!result.ok) return setMsg(result.error.message);
    setEditingTeam(null);
    setMsg(`Team ${String(body.callsign ?? team.callsign)} saved`);
    reload();
  };

  const deleteTeam = (teamId: string, callsign: string) => {
    if (delArmed !== teamId) {
      setDelArmed(teamId);
      setMsg(`Deleting ${callsign} unassigns its aircraft and members — press again to confirm`);
      return;
    }
    setDelArmed(null);
    void run(
      api.delete(`/admin/teams/${encodeURIComponent(teamId)}`, { label: `delete team ${callsign}` }),
    );
  };

  return (
    <div className="p-[18px]">
      <div className="mb-3 flex flex-wrap items-center gap-2.5">
        <span className="label-mono text-[10px]">TEAMS</span>
        <span className="label-mono text-[10.5px]" style={{ color: MUTED }}>
          {teams.length}
        </span>
        <button
          type="button"
          onClick={() => setCreating((v) => !v)}
          className="ml-auto rounded-[99px] px-[14px] py-[7px] label-mono text-[9.5px] font-bold"
          style={{ background: 'rgba(255,255,255,0.9)', color: '#0c0c0d', letterSpacing: '0.06em' }}
        >
          + NEW TEAM
        </button>
      </div>

      <SectionMsg msg={msg} />

      {creating ? (
        <TeamForm
          base={null}
          roster={roster}
          busy={busy}
          submitLabel="CREATE"
          onSubmit={(body) => void createTeam(body)}
          onCancel={() => setCreating(false)}
        />
      ) : null}

      {unassigned.length ? (
        <p className="mb-3 text-[10.5px]" style={{ color: MUTED }}>
          {unassigned.length} connected aircraft belong to no team. Every team sees them on the map;
          nobody below admin can fly them until they are assigned.
        </p>
      ) : null}

      <div
        className="grid gap-2.5"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))' }}
      >
        {teams.map((tm) => (
          <div
            key={tm.teamId}
            className="flex flex-col gap-[9px] rounded-[16px] border p-[13px]"
            style={{ background: 'rgba(255,255,255,0.035)', borderColor: 'rgba(255,255,255,0.06)' }}
          >
            <div className="flex items-center gap-[9px]">
              <span className="size-2.5 rounded-[3px]" style={{ background: tm.color }} />
              <span className="label-mono text-[11px]" style={{ letterSpacing: '0.1em' }}>
                {tm.callsign}
              </span>
              <span className="ml-auto label-mono text-[10px]" style={{ color: MUTED }}>
                {tm.droneIds.length} AC
              </span>
              <button
                type="button"
                onClick={() => setEditingTeam((v) => (v === tm.teamId ? null : tm.teamId))}
                className="rounded-[99px] border px-2 py-[2px] label-mono text-[8px]"
                style={{ color: 'rgba(244,244,245,0.6)', borderColor: 'rgba(255,255,255,0.14)' }}
              >
                EDIT
              </button>
              <button
                type="button"
                onClick={() => deleteTeam(tm.teamId, tm.callsign)}
                className="rounded-[99px] border px-2 py-[2px] label-mono text-[8px]"
                style={{
                  color: DANGER_FG,
                  borderColor: 'color-mix(in oklch, oklch(0.62 0.20 28) 50%, transparent)',
                  background:
                    delArmed === tm.teamId
                      ? 'color-mix(in oklch, oklch(0.62 0.20 28) 20%, transparent)'
                      : 'transparent',
                }}
              >
                {delArmed === tm.teamId ? 'CONFIRM' : 'DELETE'}
              </button>
            </div>
            {tm.displayName ? (
              <span className="-mt-1 text-[11px]" style={{ color: MUTED }}>
                {tm.displayName}
              </span>
            ) : null}
            <span className="tnum font-mono text-[10px]" style={{ color: MUTED }}>
              {describePorts(tm).join('  ·  ') || 'No port block'}
            </span>
            {editingTeam === tm.teamId ? (
              <TeamForm
                base={tm}
                roster={roster}
                busy={busy}
                submitLabel="SAVE"
                onSubmit={(body) => void saveTeam(tm, body)}
                onCancel={() => setEditingTeam(null)}
              />
            ) : null}
            <div className="flex flex-wrap gap-1.5">
              {tm.droneIds.map((d) => (
                <span
                  key={d}
                  className="group flex items-center gap-1 rounded-[99px] border px-[9px] py-[3px] label-mono text-[9.5px]"
                  style={{ background: 'rgba(0,0,0,0.3)', borderColor: 'rgba(255,255,255,0.1)' }}
                >
                  {d}
                  <button
                    type="button"
                    aria-label={`Unassign ${d} from ${tm.callsign}`}
                    onClick={() => void unassign(tm.teamId, d)}
                    className="text-[10px] leading-none opacity-40 hover:opacity-100"
                  >
                    ×
                  </button>
                </span>
              ))}
              {unassigned.length ? (
                <select
                  value=""
                  onChange={(e) => {
                    if (!e.target.value) return;
                    void assign(tm.teamId, e.target.value);
                    e.target.value = '';
                  }}
                  className="rounded-[99px] border bg-transparent px-[9px] py-[3px] label-mono text-[9.5px]"
                  style={{ borderColor: 'rgba(255,255,255,0.25)', borderStyle: 'dashed', color: MUTED }}
                >
                  <option value="">+ ASSIGN</option>
                  {unassigned.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {tm.memberIds.map((mid) => (
                <span
                  key={mid}
                  className="flex items-center gap-1 rounded-[99px] border px-[9px] py-[3px] text-[10.5px]"
                  style={{ borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(244,244,245,0.75)' }}
                >
                  {nameFor(mid)}
                  <button
                    type="button"
                    aria-label={`Remove ${nameFor(mid)} from ${tm.callsign}`}
                    onClick={() => void removeMember(tm.teamId, mid)}
                    className="text-[10px] leading-none opacity-40 hover:opacity-100"
                  >
                    ×
                  </button>
                </span>
              ))}
              {(() => {
                const candidates = users.filter((u) => !tm.memberIds.includes(u.userId));
                return candidates.length ? (
                  <select
                    value=""
                    onChange={(e) => {
                      if (!e.target.value) return;
                      void addMember(tm.teamId, e.target.value);
                      e.target.value = '';
                    }}
                    className="rounded-[99px] border bg-transparent px-[9px] py-[3px] label-mono text-[9px]"
                    style={{ borderColor: 'rgba(255,255,255,0.25)', borderStyle: 'dashed', color: MUTED }}
                  >
                    <option value="">+ MEMBER</option>
                    {candidates.map((u) => (
                      <option key={u.userId} value={u.userId}>
                        {u.username} ({u.role})
                      </option>
                    ))}
                  </select>
                ) : null;
              })()}
              {!tm.memberIds.length ? (
                <span className="text-[10.5px]" style={{ color: MUTED }}>
                  No members
                </span>
              ) : null}
            </div>
            <OperatorAccess team={tm} users={users} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Which of the team's aircraft each OPERATOR may command. Viewers watch,
 * tactical+ command everything on the team — only operators are scoped
 * per-aircraft, so only they appear here. Loaded lazily from the team detail
 * because the admin list endpoint doesn't carry per-member assignments.
 */
function OperatorAccess({ team, users }: { team: AdminTeam; users: AdminUser[] }) {
  const [open, setOpen] = useState(false);
  const [assigned, setAssigned] = useState<Record<string, string[]> | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const operators = users.filter(
    (u) => team.memberIds.includes(u.userId) && u.role === 'operator',
  );

  const load = async () => {
    const result = await api.get<{
      members: Array<{ userId: string; assignedDroneIds: string[] }>;
    }>(`/teams/${encodeURIComponent(team.teamId)}`, { logged: false });
    if (result.ok) {
      setAssigned(Object.fromEntries(result.data.members.map((m) => [m.userId, m.assignedDroneIds])));
    } else {
      setMsg(result.error.message);
    }
  };

  const grant = async (droneId: string, userId: string) => {
    setMsg(null);
    const result = await api.post(
      `/teams/${encodeURIComponent(team.teamId)}/drones/${encodeURIComponent(droneId)}/operator`,
      { user_id: userId },
      { label: `grant ${droneId}` },
    );
    if (!result.ok) return setMsg(result.error.message);
    void load();
  };

  const revoke = async (droneId: string, userId: string) => {
    setMsg(null);
    const result = await api.delete(
      `/teams/${encodeURIComponent(team.teamId)}/drones/${encodeURIComponent(droneId)}/operator/${encodeURIComponent(userId)}`,
      { label: `revoke ${droneId}` },
    );
    if (!result.ok) return setMsg(result.error.message);
    void load();
  };

  if (!operators.length) return null;

  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          if (!open && assigned === null) void load();
        }}
        className="self-start label-mono text-[8.5px]"
        style={{ color: MUTED }}
      >
        {open ? '▾' : '▸'} OPERATOR ACCESS — {operators.length} scoped
      </button>
      {open ? (
        <div className="flex flex-col gap-1.5">
          {msg ? (
            <span className="text-[10px]" style={{ color: DANGER_FG }}>
              {msg}
            </span>
          ) : null}
          {operators.map((op) => {
            const mine = assigned?.[op.userId] ?? [];
            const grantable = team.droneIds.filter((d) => !mine.includes(d));
            return (
              <div key={op.userId} className="flex flex-wrap items-center gap-1.5 text-[10.5px]">
                <span style={{ color: 'rgba(244,244,245,0.75)' }}>{op.username}:</span>
                {mine.map((d) => (
                  <span
                    key={d}
                    className="flex items-center gap-1 rounded-[99px] border px-2 py-[2px] label-mono text-[8.5px]"
                    style={{ borderColor: 'rgba(255,255,255,0.14)' }}
                  >
                    {d}
                    <button
                      type="button"
                      aria-label={`Revoke ${d} from ${op.username}`}
                      onClick={() => void revoke(d, op.userId)}
                      className="opacity-40 hover:opacity-100"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {!mine.length ? (
                  <span style={{ color: MUTED }}>no aircraft</span>
                ) : null}
                {grantable.length ? (
                  <select
                    value=""
                    onChange={(e) => {
                      if (!e.target.value) return;
                      void grant(e.target.value, op.userId);
                      e.target.value = '';
                    }}
                    className="rounded-[99px] border bg-transparent px-2 py-[2px] label-mono text-[8.5px]"
                    style={{ borderColor: 'rgba(255,255,255,0.25)', borderStyle: 'dashed', color: MUTED }}
                  >
                    <option value="">+ GRANT</option>
                    {grantable.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- audit

const AUDIT_FILTERS: AuditFilter[] = ['ALL', 'COMMANDS', 'ADMIN', 'PLANS', 'REFUSALS'];

function AuditTab() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [filter, setFilter] = useState<AuditFilter>('ALL');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const result = await api.get<{ entries: AuditRow[] }>('/admin/audit?limit=200', {
        logged: false,
      });
      if (result.ok) setRows(result.data.entries);
      else setError(result.error.message);
    })();
  }, []);

  const shown = useMemo(() => {
    if (filter === 'ALL') return rows;
    if (filter === 'REFUSALS') return rows.filter(auditFailed);
    const want = filter === 'COMMANDS' ? 'COMMAND' : filter === 'PLANS' ? 'PLAN' : 'ADMIN';
    return rows.filter((r) => auditKind(r) === want);
  }, [rows, filter]);

  return (
    <div className="p-[18px]">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="label-mono text-[10px]">AUDIT LOG</span>
        <span className="ml-auto flex flex-wrap gap-1.5">
          {AUDIT_FILTERS.map((f) => {
            const active = filter === f;
            return (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className="rounded-[99px] border px-2.5 py-1 label-mono text-[8.5px]"
                style={{
                  background: active ? 'rgba(255,255,255,0.9)' : 'transparent',
                  color: active ? '#0c0c0d' : MUTED,
                  borderColor: active ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.14)',
                }}
              >
                {f}
              </button>
            );
          })}
        </span>
      </div>

      {error ? <SectionMsg msg={error} /> : null}

      <div className="flex flex-col gap-[3px] label-mono text-[11px]">
        {shown.map((r) => {
          const failed = auditFailed(r);
          const kind = auditKind(r);
          return (
            <div
              key={r.entryId}
              className="flex items-baseline gap-3 rounded-[11px] px-3 py-1.5 phone:flex-wrap phone:gap-x-2.5 phone:gap-y-0.5"
              style={{
                background: failed
                  ? 'color-mix(in oklch, oklch(0.62 0.20 28) 8%, transparent)'
                  : 'rgba(255,255,255,0.03)',
              }}
            >
              <span className="whitespace-nowrap" style={{ color: 'rgba(244,244,245,0.4)' }}>
                {clock(r.timestamp)}
              </span>
              <span
                className="w-[70px] shrink-0 truncate phone:w-auto phone:max-w-[120px]"
                style={{ color: 'rgba(244,244,245,0.7)' }}
                title={r.username ?? undefined}
              >
                {r.username ?? '—'}
              </span>
              <span
                className="w-[90px] shrink-0 phone:w-auto"
                style={{ color: kind === 'COMMAND' ? WHITE : MUTED }}
              >
                {kind}
              </span>
              {/* On a phone the detail gets a line of its own: squeezed between
                  the fixed columns at 360px it had about 40px and read as "…". */}
              <span
                className="min-w-0 flex-1 truncate phone:order-last phone:basis-full"
                style={{ color: 'rgba(244,244,245,0.85)' }}
                title={r.detail || r.resource}
              >
                {r.detail || r.resource}
              </span>
              <span
                className="whitespace-nowrap phone:ml-auto"
                style={{ color: failed ? DANGER_FG : OK }}
              >
                {r.outcome.toUpperCase()}
              </span>
            </div>
          );
        })}
        {shown.length === 0 && !error ? (
          <p className="px-3 py-4 text-[11px]" style={{ color: MUTED }}>
            No audit entries for this filter.
          </p>
        ) : null}
      </div>

      <div className="mt-2.5 text-[10px]" style={{ color: 'rgba(244,244,245,0.4)' }}>
        Every mutating action is recorded server-side; this viewer only filters. Nothing here can be
        edited or deleted.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- shell

export function AdminView(): ReactNode {
  const user = useCurrentUser();
  const canAdminister = useCanAdminister();
  const isAdmin = user?.role === 'admin';
  const fleetIds = useDroneIds();
  const [tab, setTab] = useState<TabId>('accounts');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [teams, setTeams] = useState<AdminTeam[]>([]);

  const reload = useCallback(async () => {
    const [u, t] = await Promise.all([
      api.get<{ users: AdminUser[] }>('/admin/users', { logged: false }),
      api.get<{ teams: AdminTeam[] }>('/admin/teams', { logged: false }),
    ]);
    if (u.ok) setUsers(u.data.users);
    if (t.ok) setTeams(t.data.teams);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (!canAdminister) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-[13px] text-[color:var(--muted-foreground)]">
        Administration is limited to administrators.
      </div>
    );
  }

  // The assignable pool is the live fleet plus anything already on a team.
  const droneIds = [...new Set([...fleetIds, ...teams.flatMap((t) => t.droneIds)])];

  return (
    <div
      className="screen-canvas absolute inset-0"
      style={{
        background:
          'radial-gradient(1200px 700px at 70% -10%, oklch(0.19 0 0), oklch(0.12 0 0))',
      }}
    >
      <main
        className="absolute flex flex-col gap-3"
        style={{
          top: 90,
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(1060px, calc(100vw - 28px))',
        }}
      >
        <TabBar tab={tab} setTab={setTab} showAudit={isAdmin} />
        <div
          className={cn('min-h-0 flex-1 overflow-y-auto rounded-[20px] border scroll-thin')}
          style={{
            background: CARD,
            backdropFilter: 'blur(28px) saturate(160%)',
            borderColor: 'rgba(255,255,255,0.13)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.15)',
          }}
        >
          {tab === 'accounts' ? (
            <AccountsTab
              users={users}
              teams={teams}
              selfId={user?.userId ?? null}
              isAdmin={isAdmin}
              myTeamIds={user?.teamIds ?? []}
              reload={reload}
            />
          ) : null}
          {tab === 'teams' ? (
            <TeamsTab teams={teams} users={users} droneIds={droneIds} reload={reload} />
          ) : null}
          {tab === 'audit' && isAdmin ? <AuditTab /> : null}
          {tab === 'network' && isAdmin ? <NetworkTab /> : null}
          {tab === 'nodes' && isAdmin ? <NodesTab /> : null}
        </div>
      </main>
    </div>
  );
}

export default AdminView;
